// Portal 到 body 的下拉面板，DOM 位置離觸發鈕很遠，鍵盤 Tab 走不進去（2026-10-13 拍板：Tab 不到的要補）。
//   - 鍵盤開啟（最近一次操作是鍵盤）時，焦點移到面板內第一個可聚焦元素；
//     面板自己已聚焦（例如搜尋框自動聚焦）就不搶。滑鼠開啟不動焦點，畫面同舊版。
//   - 焦點在面板內時按 Tab／Shift+Tab 走出面板邊界 → 關閉面板、焦點回到觸發鈕：
//     Shift+Tab 停在觸發鈕；Tab 不擋預設動作，瀏覽器從觸發鈕接著往下走到下一個元素。
//   - 開啟當下焦點在文字輸入框（打字帶出的建議清單）不搬焦點，免得打字被打斷。
//   - 鍵盤開啟的面板關閉後（選了項目、Esc），焦點若掉到 body 就還給觸發鈕。
// 用法：usePanelFocus(open, panelRef, close, { initial })；panelRef 指向面板根元素，
//   initial＝鍵盤開啟時優先聚焦的選擇器陣列，依序找（例如日曆：已選日 → 今天），都找不到才落到第一個可聚焦元素。
import { useEffect, useRef } from 'react'

const FOCUSABLE = 'input:not([disabled]):not([type=hidden]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'

// 全站追蹤兩件事（模組載入時掛一次）：最近一次操作是不是鍵盤、上一個失去焦點的元素。
// 面板內有 autoFocus 的搜尋框時，effect 跑的時候焦點已經在面板裡，靠這兩個找回觸發鈕與開啟方式
let keyboardModality = false
let lastBlurred = null
if (typeof document !== 'undefined') {
  document.addEventListener('keydown', () => (keyboardModality = true), true)
  document.addEventListener('pointerdown', () => (keyboardModality = false), true)
  document.addEventListener('focusin', (e) => (lastBlurred = e.relatedTarget), true)
}

// 浮層開啟當下「開啟前的焦點」：子元件 autoFocus 會在 effect 前就把焦點搬進浮層，這時改用上一個失焦元素（觸發鈕）
export const focusBeforeOpen = (container) => {
  const active = document.activeElement
  return container?.contains(active) ? lastBlurred : active
}

const isTextInput = (el) => el instanceof HTMLElement && (el.matches('input:not([type=button]):not([type=checkbox]):not([type=radio]), textarea') || el.isContentEditable)

export function usePanelFocus(open, panelRef, close, { initial } = {}) {
  const closeRef = useRef(close)
  useEffect(() => {
    closeRef.current = close
  })

  useEffect(() => {
    if (!open) return
    const panel = panelRef.current
    if (!panel) return
    const active = document.activeElement
    const opener = focusBeforeOpen(panel)
    const keyboard = keyboardModality && opener instanceof HTMLElement && opener !== document.body && !panel.contains(opener) && !isTextInput(opener)
    if (keyboard && !panel.contains(active)) {
      const first = (initial ?? []).map((sel) => panel.querySelector(sel)).find(Boolean)
      ;(first ?? panel.querySelector(FOCUSABLE))?.focus({ preventScroll: true })
    }

    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return
      // 以 DOM 位置判斷這一步是否還留在面板內（焦點可能在 tabindex=-1 的元素上，例如日曆的日期格）
      const active = document.activeElement
      const list = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el !== active && el.offsetParent !== null)
      const dir = e.shiftKey ? Node.DOCUMENT_POSITION_PRECEDING : Node.DOCUMENT_POSITION_FOLLOWING
      const ahead = list.filter((el) => active.compareDocumentPosition(el) & dir)
      if (ahead.length) {
        // 焦點在 tabindex=-1 的元素上（日曆日期格）：自己移到最近的下一個，別讓元件內建的 Tab 處理吃掉（flatpickr 的 Shift+Tab 會把焦點丟給不可聚焦的宿主）
        if (active.tabIndex < 0) {
          e.preventDefault()
          e.stopPropagation()
          ;(e.shiftKey ? ahead[ahead.length - 1] : ahead[0]).focus()
        }
        return
      }
      if (!(opener instanceof HTMLElement) || !opener.isConnected || opener === document.body || panel.contains(opener)) return
      if (e.shiftKey) e.preventDefault()
      closeRef.current?.()
      opener.focus({ preventScroll: true })
    }
    panel.addEventListener('keydown', onKeyDown)
    return () => {
      panel.removeEventListener('keydown', onKeyDown)
      if (!keyboard) return
      // 同步還原（不等下一影格）：選項若接著打開彈窗，彈窗的 useInertBackground 才記得到觸發鈕、關閉後還得回來
      const cur = document.activeElement
      if ((!cur || cur === document.body) && opener.isConnected) opener.focus({ preventScroll: true })
    }
    // initial 是呼叫端寫死的選擇器常數
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, panelRef])
}
